// Shared by POST /api/images/bulk and the media library client.

export const BULK_IMAGE_ACTIONS = [
  'REMOVE',
  'RESTORE',
  'FAVORITE',
  'UNFAVORITE',
] as const;

export type BulkImageAction = (typeof BULK_IMAGE_ACTIONS)[number];

/** Clients send larger selections in batches of this size. */
export const MAX_BULK_IMAGE_IDS = 500;

export interface BulkImageActionResult {
  affected_ids: number[];
  skipped_ids: number[];
}

export const isBulkImageAction = (value: unknown): value is BulkImageAction =>
  BULK_IMAGE_ACTIONS.includes(value as BulkImageAction);

/** Positive integer ids (numbers or digit strings), de-duplicated; null if malformed. */
export const normalizeBulkImageIds = (value: unknown): number[] | null => {
  if (!Array.isArray(value) || value.length === 0) return null;
  const ids = new Set<number>();
  for (const raw of value) {
    const id = typeof raw === 'string' && /^\d+$/.test(raw) ? Number(raw) : raw;
    if (typeof id !== 'number' || !Number.isSafeInteger(id) || id <= 0)
      return null;
    ids.add(id);
  }
  return Array.from(ids);
};

/**
 * Runs a bulk action over any number of ids in sequential batches and merges
 * the results. `post` performs one request (axios in the app, a stub in tests).
 * A failed batch stops the run and rethrows with what already succeeded
 * attached, so the caller can still reflect the partial result.
 */
export const runBulkImageAction = async (
  action: BulkImageAction,
  ids: number[],
  post: (body: {
    action: BulkImageAction;
    image_ids: number[];
  }) => Promise<BulkImageActionResult>,
  onBatch?: (done: number, total: number) => void,
): Promise<BulkImageActionResult> => {
  const result: BulkImageActionResult = { affected_ids: [], skipped_ids: [] };
  for (let start = 0; start < ids.length; start += MAX_BULK_IMAGE_IDS) {
    const batch = ids.slice(start, start + MAX_BULK_IMAGE_IDS);
    try {
      const response = await post({ action, image_ids: batch });
      result.affected_ids.push(...(response.affected_ids || []));
      result.skipped_ids.push(...(response.skipped_ids || []));
    } catch (error) {
      throw Object.assign(
        error instanceof Error ? error : new Error(String(error)),
        { partialResult: result },
      );
    }
    onBatch?.(Math.min(start + batch.length, ids.length), ids.length);
  }
  return result;
};
